import React, { useState } from "react";
import { Check, Loader2, X, Sparkles } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../../services/api";
import tokenManager from "../../utils/tokenManager";

// ─── Demo Login Modal ────────────────────────────────────────────────────────

const DemoModal = ({ onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiService.demoLogin({ name, email, companyName });
      if (response.success) {
        window.dispatchEvent(new Event("userChanged"));
        onSuccess();
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to start demo. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl border border-[#22262F] p-8"
        style={{ background: "#13161B" }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              }}
            >
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="text-xs font-medium text-purple-400 uppercase tracking-wider">
              Free Preview
            </span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-1">
            Try the demo
          </h2>
          <p className="text-sm text-gray-400">
            Explore the full platform with pre-loaded data. No credit card
            needed.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Your name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="e.g. Sarah Johnson"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Work email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="you@company.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Company name
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="e.g. Acme Corp"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-2 relative hover:scale-[1.02] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                0px 36px 32px -16px #7F56D936,
                0px 8px 16px 0px #7F56D91F,
                0px 2px 4px -8px #7F56D914,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
              borderImageSlice: "1",
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Setting up your demo...
                </>
              ) : (
                "Start free demo"
              )}
            </span>
          </button>
        </form>

        <p className="text-xs text-gray-600 text-center mt-4">
          Demo data is reset nightly. Your details won't be shared.
        </p>
      </div>
    </div>
  );
};

// ─── Main Login Component ────────────────────────────────────────────────────

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDemoModal, setShowDemoModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = searchParams.get("returnUrl");

  const handleForgot = () => {
    navigate("/forgot-password");
  };

  const handleGoogleLogin = () => {
    const baseUrl = `${
      process.env.REACT_APP_API_URL || "http://localhost:5000"
    }/api/auth/google`;

    if (returnUrl) {
      window.location.href = `${baseUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
    } else {
      window.location.href = baseUrl;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiService.login(email, password, remember);

      if (response.success) {
        tokenManager.setUser(response.user);
        window.dispatchEvent(new Event("userChanged"));

        if (returnUrl) {
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 100);
          return;
        }

        if (!response.user.company?.workspaceUrl) {
          navigate("/onboarding");
        } else {
          navigate("/overview");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.message || "Failed to login. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDemoSuccess = () => {
    navigate("/overview");
  };

  return (
    <>
      {showDemoModal && (
        <DemoModal
          onClose={() => setShowDemoModal(false)}
          onSuccess={handleDemoSuccess}
        />
      )}

      <div className="max-h-screen flex flex-col lg:flex-row">
        {/* Left side - Login Form */}
        <div className="w-full lg:w-2/5 flex items-center justify-center px-6 sm:px-8 md:px-12 py-8 lg:py-0">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="mb-8 sm:mb-12 lg:mb-16">
              <img
                src="/assets/logo.png"
                alt="Logo"
                className="h-[50px] w-auto"
              />
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-2 sm:mb-3">
                Log in
              </h1>

              {returnUrl && returnUrl.includes("/integrations/authorize") ? (
                <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
                  Please log in to authorize the integration
                </p>
              ) : (
                <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
                  Welcome back! Please enter your details.
                </p>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-4 sm:space-y-5">
                {/* Email Field */}
                {/* <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div> */}

                {/* Password Field */}
                {/* <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                </div> */}

                {/* Remember me and Forgot password */}
                {/* <div className="flex items-center justify-between py-2 flex-wrap gap-2">
                  <label className="flex items-center">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="appearance-none w-4 h-4 rounded border border-gray-600 bg-[#0C0E12] checked:bg-purple-500 checked:border-purple-500 focus:ring-purple-500 focus:ring-2"
                        disabled={loading}
                      />
                      {remember && (
                        <Check
                          size={11}
                          className="absolute top-1 left-0.5 text-white pointer-events-none"
                        />
                      )}
                    </div>
                    <span className="ml-2 text-xs sm:text-sm text-white">
                      Remember for 30 days
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-xs sm:text-sm text-white hover:text-purple-300 bg-transparent border-none cursor-pointer"
                    onClick={handleForgot}
                    disabled={loading}
                  >
                    Forgot password
                  </button>
                </div> */}

                {/* Sign in Button */}
                {/* <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 sm:py-3 text-white rounded-lg font-medium transition-all duration-300 mt-4 sm:mt-6 relative 
                    hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                    border: "1px solid",
                    borderImageSource:
                      "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: `
                      0px 36px 32px -16px #7F56D936,
                      0px 8px 16px 0px #7F56D91F,
                      0px 2px 4px -8px #7F56D914,
                      0px 8px 24px -16px #FFFFFF3D inset,
                      0px -24px 32px 0px #FFFFFF38 inset,
                      0px 0px 12px 0px #FFFFFF inset
                    `,
                    borderImageSlice: "1",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
                      e.currentTarget.style.boxShadow = `
                        0px 42px 36px -12px #8D5EE680,
                        0px 12px 20px 0px #7F56D93D,
                        0px 4px 8px -4px #7F56D928,
                        0px 12px 28px -12px #7F56D9 inset,
                        0px -28px 36px 0px #FFFFFF4D inset,
                        0px 0px 16px 0px #FFFFFF inset
                      `;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
                    e.currentTarget.style.boxShadow = `
                      0px 36px 32px -16px #7F56D936,
                      0px 8px 16px 0px #7F56D91F,
                      0px 2px 4px -8px #7F56D914,
                      0px 8px 24px -16px #FFFFFF3D inset,
                      0px -24px 32px 0px #FFFFFF38 inset,
                      0px 0px 12px 0px #FFFFFF inset
                    `;
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        Signing in...
                      </>
                    ) : (
                      "Sign in"
                    )}
                  </span>
                </button> */}

                {/* Google Sign in */}
                {/* <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full py-2.5 sm:py-3 bg-gray-800 border border-gray-700 rounded-lg font-medium text-white flex items-center justify-center gap-3 hover:bg-gray-750 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </button> */}

                {/* Demo CTA */}
                {!returnUrl?.includes("/integrations/authorize") && (
                  <>
                    <div className="relative flex items-center gap-3 pt-1">
                      <div className="flex-1 h-px bg-gray-800" />
                      <span className="text-xs text-gray-600 whitespace-nowrap">
                        or
                      </span>
                      <div className="flex-1 h-px bg-gray-800" />
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowDemoModal(true)}
                      disabled={loading}
                      className="w-full py-2.5 sm:py-3 bg-transparent border border-gray-700 hover:border-gray-500 rounded-lg font-medium text-gray-300 hover:text-white flex items-center justify-center gap-2 transition-all duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles size={15} className="text-purple-400" />
                      Try a free demo
                    </button>
                  </>
                )}

                {/* Sign up link */}
                {/* {!returnUrl?.includes("/integrations/authorize") && (
                  <p className="text-center text-xs sm:text-sm text-gray-400 mt-4 sm:mt-6">
                    Don't have an account?{" "}
                    <button
                      className="text-white hover:text-purple-300 font-medium bg-transparent border-none cursor-pointer"
                      onClick={() => navigate("/onboarding")}
                      disabled={loading}
                    >
                      Sign up
                    </button>
                  </p>
                )} */}
              </div>
            </form>

            {/* Footer */}
            <div className="mt-12 sm:mt-16 lg:mt-20">
              <p className="text-xs sm:text-sm text-gray-500">
                © OctopushX Ltd 2026
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Dashboard Image */}
        <div className="hidden lg:flex w-3/5 bg-[#13161B] items-center justify-center pt-[100px] pb-[100px] pl-[100px] pr-0 rounded-l-[42px] mt-5 mb-5 h-[90vh]">
          <div className="w-full h-full rounded-l-[10px] overflow-hidden border-l-[4px] border-t-[4px] border-b-[4px] border-[#22262F]">
            <img
              src="/assets/banner2.png"
              alt="Dashboard Preview"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
